'use client';

// ============================================================
// LA METAMORPHOSE : entrer dans un univers, pas changer de page
// ------------------------------------------------------------
// CE QUE C'EST. Au clic sur un univers, le point exact du geste est note dans un
// temoin, la navigation part, et l'espace d'ARRIVEE ouvre EN DEUX BATTANTS un
// aplat de la couleur de la marque, le long d'une fente qui passe par ce point.
// Le hub apparait dans l'ouverture. La geometrie et le dessin vivent dans
// espace-styles.tsx (section « LA PORTE DE L'UNIVERS »), avec la justification
// complete de ce choix et le releve de ce qui n'allait pas dans l'onde d'avant.
//
// POURQUOI L'EFFET EST JOUE A L'ARRIVEE, ET PAS AU DEPART. Le lanceur avait son
// voile (lanceur-ouverture.tsx), mais il etait joue sur la page qu'on QUITTE :
// des que la route repondait, il disparaissait au milieu de sa course, et plus
// le reseau etait bon, moins on voyait quoi que ce soit. Depuis qu'un univers a
// son propre hote, la bascule est en plus une navigation de DOCUMENT : rien de
// ce qui est joue au depart ne peut survivre. Le geste est donc enregistre au
// depart, et le mouvement est joue par la page d'arrivee, qui a du temps devant
// elle.
//
// LES CINQ GARDE-FOUS, tenus ici et verifiables dans ce fichier :
//
//   1. CA NE FAIT JAMAIS ATTENDRE. Rien n'est retarde, aucune requete n'est
//      differee, aucune attente n'est ouverte. La porte est purement decorative :
//      `pointer-events:none` sur le calque ET sur chacun de ses enfants, montee
//      APRES le decor, et le contenu est utilisable dessous pendant toute la
//      course. Trois filets INDEPENDANTS la retirent : le temoin est consomme des
//      sa lecture, un minuteur demonte le calque quoi qu'il arrive, et l'etat de
//      REPOS des trois couches est deja invisible (voir la feuille de style), si
//      bien qu'un calque oublie ne se verrait meme pas.
//   2. `prefers-reduced-motion: reduce` COURT-CIRCUITE TOUT : rien n'est monte
//      ici, rien n'est meme ecrit au depart, et la feuille de style pose la
//      ceinture au cas ou le reglage change pendant la course.
//   3. SEULEMENT A L'ENTREE DANS UN UNIVERS. Le declencheur est le geste de
//      bascule, jamais une navigation. Aller de /clients a /contrats n'ecrit
//      aucun temoin, donc ne joue rien.
//   4. UNE SEULE FOIS. Le temoin est efface au moment meme ou il est lu, dans
//      ses DEUX depots, et il porte l'espace vise plus un horodatage : on ne
//      rejoue rien sur un rechargement, ni sur un retour en arriere.
//   5. AUCUNE COULEUR ECRITE ICI. Les battants lisent --acc, --acc-d, --acc2 et
//      le sceau lit --acc-on, toutes posees par EspaceTheme depuis l'identite
//      visuelle de la societe. Un troisieme univers cree demain a sa porte a ses
//      couleurs sans qu'une ligne change.
// ============================================================

import { useEffect, useState, type CSSProperties } from 'react';
import { domaineDePartage } from '@/lib/hote-espace';
import { useEspaceCourant } from './espace-theme';
import { FiligraneMarque } from './marque-typographie';

const CLE = 'vyndra_metamorphose';

/**
 * Le temoin est AUSSI depose en cookie sur le domaine partage, et c'est
 * indispensable depuis que les espaces vivent sur leur propre sous-domaine :
 * `sessionStorage` est propre a une ORIGINE. Partir de app.vyndra.fr pour
 * arriver sur guardinformatique.vyndra.fr faisait donc perdre le geste, et
 * l'effet ne se jouait plus jamais lors d'une bascule d'univers, c'est-a-dire
 * exactement dans le cas ou il sert.
 *
 * Le cookie ne vit que quelques secondes, il ne porte aucun secret (un point
 * touche et un slug), et il est efface des sa lecture comme le sessionStorage.
 */
const CLE_COOKIE = 'vyndra_metamorphose';

/** Duree de vie du cookie : a peine plus que la validite du geste. */
const DUREE_COOKIE_SECONDES = 5;

/** Au-dela, le geste est trop vieux pour etre le meme : on ne rejoue rien. */
const VALIDITE_MS = 2500;

/**
 * MAJORANT de la duree du mouvement, et rien de plus.
 *
 * Les durees reelles vivent dans la feuille de style : 580 ms pour les battants
 * et le sceau (--mtm-t), 860 ms pour l'allumage des aretes de la piece. C'est la
 * qu'on regle un mouvement. Ce nombre-ci ne sert qu'au filet en temps, il doit
 * seulement rester SUPERIEUR a la plus longue des animations. Le raccourcir ne
 * casse rien de visible (l'etat de repos est invisible), l'allonger ne bloque
 * rien non plus (le calque ne capte aucun clic).
 */
const MOUVEMENT_MAX_MS = 1000;

/** Marge du filet, pour ne pas couper une fin d'animation sur une machine chargee. */
const MARGE_FILET_MS = 350;

/**
 * Marqueur pose sur la racine du document pendant la course. Il permet a la
 * feuille de style d'allumer les deux aretes de la piece (la tranche du menu et
 * le filet sous la barre du haut), qui sont des FRERES de ce composant et non
 * ses enfants. Voir la justification dans espace-styles.tsx.
 */
const CLASSE_RACINE = 'vyndra-mtm';

/**
 * Inclinaison de la fente, en degres, telle qu'elle est ecrite dans la feuille
 * de style (--mtm-a). Elle est REPRISE ici pour un seul calcul : la taille que
 * doit avoir chaque battant pour couvrir sa moitie d'ecran puis en sortir.
 *
 * LA FEUILLE DE STYLE PEUT LA REDRESSER SANS RISQUE, et elle le fait deja au
 * doigt (--mtm-a:0deg sous 640 px). Le calcul reste alors LEGEREMENT trop
 * genereux, jamais trop juste : redresser la fente ne peut que raccourcir les
 * distances a couvrir. Un battant deborde donc de quelques pixels au lieu de
 * laisser voir une bande, ce qui est le bon sens de l'erreur.
 */
const ANGLE_FENTE_DEG = -13;

/** Marge ajoutee a la course d'un battant, pour que son liseré sorte vraiment. */
const DEBORD_PX = 48;

/** Hauteur plancher d'un battant : un geste tout au bord en donnerait un ras. */
const HAUTEUR_MINI_PX = 72;

export interface GesteBascule {
  /** Espace vise : la porte ne s'ouvre que si l'on arrive bien la. */
  slug: string;
  /** Point touche, en coordonnees d'ecran. */
  x: number;
  y: number;
  /** Horodatage du geste. */
  t: number;
}

/** Vrai quand la personne a demande moins d'animations au systeme. */
function mouvementReduit(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Enregistre le geste de bascule, juste avant de naviguer.
 *
 * Appele par les DEUX portes d'entree, et c'est voulu : le selecteur d'espace
 * du menu, et les cartes du lanceur. Les deux doivent donner la meme sensation.
 *
 * On n'ecrit rien quand le mouvement est reduit : inutile de deposer un temoin
 * que personne ne lira.
 */
export function noterGesteBascule(slug: string, x: number, y: number): void {
  if (typeof window === 'undefined' || mouvementReduit()) return;
  const geste: GesteBascule = { slug, x, y, t: Date.now() };
  const brut = JSON.stringify(geste);
  try {
    window.sessionStorage.setItem(CLE, brut);
  } catch {
    // Stockage refuse (navigation privee stricte) : on perd l'effet, jamais la
    // navigation. C'est exactement la hierarchie voulue.
  }
  // Et le meme geste en cookie du domaine partage, seule facon de le retrouver
  // quand la bascule change d'hote.
  ecrireGesteEnCookie(brut);
}

/** Attributs de securite du cookie, Secure seulement en HTTPS (developpement local). */
function securiteDuCookie(): string {
  return typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
}

/** Fragment « ; Domain=.vyndra.fr », ou chaine vide en local. */
function fragmentDomaine(): string {
  if (typeof location === 'undefined') return '';
  const domaine = domaineDePartage(location.host);
  return domaine === null ? '' : `; Domain=${domaine}`;
}

function ecrireGesteEnCookie(brut: string): void {
  if (typeof document === 'undefined') return;
  try {
    document.cookie =
      `${CLE_COOKIE}=${encodeURIComponent(brut)}; Path=/; Max-Age=${DUREE_COOKIE_SECONDES}; SameSite=Lax${securiteDuCookie()}${fragmentDomaine()}`;
  } catch {
    // Cookies refuses : on perd l'effet, jamais la navigation.
  }
}

/**
 * Efface le cookie du geste. DEUX effacements : la version du domaine partage
 * et la version propre a l'hote. Un cookie pose avec un Domain ne se supprime
 * que si l'effacement precise le meme Domain, et sans cela la porte se rejouerait
 * a chaque rechargement pendant cinq secondes.
 */
function effacerGesteEnCookie(): void {
  if (typeof document === 'undefined') return;
  const securite = securiteDuCookie();
  document.cookie = `${CLE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${securite}`;
  const domaine = fragmentDomaine();
  if (domaine.length > 0) {
    document.cookie = `${CLE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${securite}${domaine}`;
  }
}

/** Lit le geste depuis le cookie du domaine partage, ou null. */
function gesteDepuisCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const trouve = document.cookie
    .split('; ')
    .find((paire) => paire.startsWith(`${CLE_COOKIE}=`));
  if (!trouve) return null;
  const valeur = trouve.slice(CLE_COOKIE.length + 1);
  try {
    return decodeURIComponent(valeur);
  } catch {
    return valeur;
  }
}

/**
 * Lit le geste ET l'efface dans le meme mouvement : il ne peut pas etre rejoue.
 *
 * Deux sources, dans cet ordre : le sessionStorage quand on est reste sur le
 * meme hote, le cookie du domaine partage quand la bascule a change d'hote. Les
 * DEUX sont effacees quoi qu'il arrive, sinon un geste oublie d'un cote
 * rejouerait la porte a la navigation suivante.
 */
function consommerGeste(slug: string): GesteBascule | null {
  if (typeof window === 'undefined') return null;
  let brut: string | null = null;
  try {
    brut = window.sessionStorage.getItem(CLE);
    if (brut !== null) window.sessionStorage.removeItem(CLE);
  } catch {
    brut = null;
  }
  if (brut === null) brut = gesteDepuisCookie();
  effacerGesteEnCookie();
  if (brut === null) return null;
  try {
    const geste = JSON.parse(brut) as GesteBascule;
    if (geste?.slug !== slug) return null;
    if (typeof geste.t !== 'number' || Date.now() - geste.t > VALIDITE_MS) return null;
    if (typeof geste.x !== 'number' || typeof geste.y !== 'number') return null;
    return geste;
  } catch {
    return null;
  }
}

/** Geometrie des deux battants, en pixels, pour un geste et une fenetre donnes. */
export interface GeometriePorte {
  /** Abscisse retenue du point du geste, ramenee dans la fenetre. */
  x: number;
  /** Ordonnee retenue du point du geste, ramenee dans la fenetre. */
  y: number;
  /** Largeur commune des deux battants. */
  largeur: number;
  /** Hauteur du battant du haut, egale a sa course. */
  hauteurHaut: number;
  /** Hauteur du battant du bas, egale a sa course. */
  hauteurBas: number;
}

/**
 * Calcule la taille des deux battants pour que chacun couvre entierement sa
 * moitie de l'ecran au depart, et en soit entierement sorti a l'arrivee.
 *
 * COMMENT. La fente passe par le point du geste, inclinee de ANGLE_FENTE_DEG. La
 * course utile d'un battant est la plus grande distance PERPENDICULAIRE a cette
 * fente entre le point du geste et un coin de la fenetre, de son cote. On la
 * mesure sur les quatre coins, ce qui donne les deux courses d'un coup, et la
 * hauteur du battant vaut cette course (dans la feuille de style, il recule de
 * exactement 100 pour cent de sa hauteur).
 *
 * POURQUOI CALCULE ET PAS EN DUR. Un « 200vmax » aurait suffi a l'oeil mais
 * aurait fabrique deux calques enormes pour rien, sur toutes les machines, y
 * compris un portable modeste. Ici chaque battant fait exactement la taille
 * necessaire, ce qui est aussi la raison pour laquelle le mouvement tient sans
 * effort : deux surfaces justes, transportees par le compositeur.
 *
 * LE POINT EST RAMENE DANS LA FENETRE, et ce n'est pas de la precaution
 * gratuite : le geste a ete fait sur une AUTRE page, potentiellement avant une
 * rotation d'ecran ou un redimensionnement. Un point hors cadre donnerait une
 * fente hors de l'ecran, donc une porte qui ne s'ouvre pas.
 */
export function geometriePorte(
  xBrut: number,
  yBrut: number,
  largeurFenetre: number,
  hauteurFenetre: number,
): GeometriePorte {
  const l = Math.max(1, largeurFenetre);
  const h = Math.max(1, hauteurFenetre);
  const x = Math.min(Math.max(xBrut, 0), l);
  const y = Math.min(Math.max(yBrut, 0), h);

  const a = (ANGLE_FENTE_DEG * Math.PI) / 180;
  // Normale a la fente : les distances mesurees le long de cet axe sont les
  // courses des battants. Positif du cote du bas, negatif du cote du haut.
  const nx = -Math.sin(a);
  const ny = Math.cos(a);

  let versLeBas = 0;
  let versLeHaut = 0;
  // Et le long de la fente, pour la largeur. ATTENTION, LE PIEGE EST ICI : le
  // battant est CENTRE sur le point du geste, donc sa demi-largeur doit couvrir
  // le coin le plus eloigne le long de l'axe de la fente. Un geste dans le coin
  // gauche demande donc presque toute la diagonale d'un seul cote, et une largeur
  // calculee « en moyenne » laissait une bande de hub visible a droite avant
  // meme l'ouverture.
  let demiLargeur = 0;
  for (const [cx, cy] of [[0, 0], [l, 0], [0, h], [l, h]] as const) {
    const perpendiculaire = (cx - x) * nx + (cy - y) * ny;
    if (perpendiculaire > versLeBas) versLeBas = perpendiculaire;
    if (-perpendiculaire > versLeHaut) versLeHaut = -perpendiculaire;
    const leLongDeLaFente = Math.abs((cx - x) * Math.cos(a) + (cy - y) * Math.sin(a));
    if (leLongDeLaFente > demiLargeur) demiLargeur = leLongDeLaFente;
  }

  const largeur = Math.ceil(2 * demiLargeur + 2 * DEBORD_PX);

  return {
    x,
    y,
    largeur,
    hauteurHaut: Math.max(HAUTEUR_MINI_PX, Math.ceil(versLeHaut) + DEBORD_PX),
    hauteurBas: Math.max(HAUTEUR_MINI_PX, Math.ceil(versLeBas) + DEBORD_PX),
  };
}

/**
 * La porte de l'univers, ouverte une seule fois a l'arrivee dans un espace.
 *
 * Elle est montee DANS le conteneur d'espace : elle herite donc de --acc, --acc2
 * et --acc-on sans qu'aucune couleur ne soit ecrite ici ni passee en propriete.
 */
export function MetamorphoseEspace({ slug }: { slug: string }) {
  const [geste, setGeste] = useState<GesteBascule | null>(null);
  // Le logo de l'identite, pour le sceau. Il vient du contexte d'espace, la meme
  // source que la tete du menu : deux emplacements ne peuvent donc pas afficher
  // deux logos differents. Absent, FiligraneMarque ne rend rien, et la porte
  // s'ouvre sans sceau plutot qu'avec un monogramme, que le dirigeant a ecarte
  // explicitement pour les filigranes.
  const identite = useEspaceCourant();
  const jeuLogos = identite?.jeuLogos ?? null;

  useEffect(() => {
    if (mouvementReduit()) return;
    const trouve = consommerGeste(slug);
    if (trouve) setGeste(trouve);
  }, [slug]);

  // FILET DE SECURITE, ET IL EST DANS SON PROPRE EFFET, VOLONTAIREMENT.
  //
  // Il etait arme dans l'effet ci-dessus, juste apres la lecture du geste. Bug
  // constate au navigateur : en developpement, React monte, demonte et remonte
  // chaque effet une fois. Le premier passage consommait le geste et armait le
  // minuteur, le demontage l'annulait, et le second passage ne trouvait plus
  // rien a consommer : il n'armait donc AUCUN minuteur. Le voile restait pose sur
  // l'ecran indefiniment.
  //
  // Rattache a `geste`, le minuteur est re-arme a chaque fois qu'une porte est
  // posee, quel que soit le nombre de passages. Il couvre aussi le cas ou une fin
  // d'animation ne se declencherait jamais : onglet passe en arriere-plan pendant
  // la course, ou animation neutralisee par le reglage systeme.
  //
  // C'est aussi CE MINUTEUR, et lui seul, qui retire le marqueur de la racine du
  // document. On n'ecoute aucun evenement de fin d'animation : sur quatre
  // couches il en arrive quatre, dont l'ordre depend du navigateur, et une seule
  // manquante suffirait a laisser le marqueur en place.
  useEffect(() => {
    if (!geste) return;
    const racine = document.documentElement;
    racine.classList.add(CLASSE_RACINE);
    const filet = window.setTimeout(
      () => setGeste(null),
      MOUVEMENT_MAX_MS + MARGE_FILET_MS,
    );
    return () => {
      window.clearTimeout(filet);
      racine.classList.remove(CLASSE_RACINE);
    };
  }, [geste]);

  if (!geste) return null;

  const porte = geometriePorte(
    geste.x,
    geste.y,
    typeof window === 'undefined' ? 1440 : window.innerWidth,
    typeof window === 'undefined' ? 900 : window.innerHeight,
  );

  return (
    <div
      className="esp-mtm"
      aria-hidden="true"
      style={
        {
          '--mtm-x': `${porte.x}px`,
          '--mtm-y': `${porte.y}px`,
          '--mtm-lw': `${porte.largeur}px`,
          '--mtm-hh': `${porte.hauteurHaut}px`,
          '--mtm-hb': `${porte.hauteurBas}px`,
        } as CSSProperties
      }
    >
      <div className="esp-mtm-volet esp-mtm-haut" />
      <div className="esp-mtm-volet esp-mtm-bas" />
      {/* Le sceau : le logo de la marque en silhouette, importe et non recopie.
          Peint en --acc-on, donc lisible sur la couleur de la marque quelle
          qu'elle soit. L'opacite et l'echelle de la course sont portees par
          .esp-mtm-signe, dans la feuille de style. */}
      <FiligraneMarque
        jeu={jeuLogos}
        reglagesEmplacements={identite?.reglagesEmplacements ?? null}
        className="esp-mtm-signe"
        taille="min(52vmin, 460px)"
        opacite={1}
        couleur="var(--acc-on, #FFFFFF)"
        cadrage="signe"
      />
    </div>
  );
}
